<template>
  <div>
    <b-skeleton-table
      v-if="isLoading"
      :rows="5"
      :columns="5"
    />
    <b-table
      v-else
      ref="refGiftProductListTable"
      :items="cartProducts.data"
      responsive
      :fields="tableFields"
      show-empty
      empty-text="Tidak ada data yang ditemukan"
      class="position-relative mh-160"
    >
      <!-- Column: Nama -->
      <template #cell(name)="data">
        <b-media
          v-if="data.item.product"
          vertical-align="center"
        >
          <template #aside>
            <b-img
              rounded
              width="50"
              :src="linkStaging(data.item.product.images[0].image_url, 50, 50)"
              alt="Product Image"
              @error="(event) => {
                event.target.src = require('@/assets/images/icons/broken-image.svg')
              }"
            />
          </template>
          <span class="font-weight-bold d-block">
            {{ data.item.product.name }}
          </span>
          <small class="text-muted">Ordered by {{ data.item.user.name }}</small>
        </b-media>
      </template>

      <!-- Column: Pembeli -->
      <template #cell(user)="data">
        <div v-if="data.item.user && data.item.user.email">
          <b-badge
            v-b-tooltip.hover.top="'Kirim Email'"
            target="_blank"
            :href="`mailto:${data.item.email}`"
            class="mb-1"
            variant="light-secondary"
          >
            <feather-icon
              icon="MailIcon"
              class="mr-25"
            />
            <span>{{ data.item.user.email }}</span>
          </b-badge>
        </div>

        <div v-if="data.item.user && data.item.user.phone">
          <b-badge
            v-b-tooltip.hover.top="'Kirim Pesan WhatsApp'"
            target="_blank"
            :href="`https://wa.me/${data.item.user.phone}`"
            variant="light-success"
          >
            <img
              :src="require('@/assets/images/icons/whatsapp.svg')"
              class="mr-25 feather"
              width="16px"
            >
            <span>{{ data.item.user.phone }}</span>
          </b-badge>
        </div>
      </template>

      <!-- Column: Variant -->
      <template #cell(variant)="data">
        <span
          v-if="data.item.variant.variant"
          class="text-nowrap"
        >
          {{ data.item.variant.variant }}
        </span>
        <span
          v-else-if="data.item.variant.sku_name"
          class="text-nowrap"
        >
          {{ data.item.variant.sku_name }}
        </span>
        <i
          v-else
          class="text-nowrap text-secondary"
        >Tanpa Nama</i>
      </template>

      <!-- Column: Price -->
      <template #cell(price)="data">
        <span class="text-nowrap">
          {{ toCurrency(data.item.variant.price) }}
        </span>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <table-action
            :actions="tableAction"
            :data="data"
          />
        </div>
      </template>

    </b-table>
    <!-- Footer -->
    <div class="mx-2 mb-2">
      <pagination
        :data="cartProducts.meta"
        action="cartProducts/getCartProducts"
      />
    </div>

    <b-modal
      v-model="isShowEditVariant"
      title="Ubah Variant"
      hide-footer
      centered
    >
      <div v-if="activeCartProduct">
        <div>{{ activeCartProduct.product.name }}</div>
        <hr>
        <b-row>
          <b-col
            xl="3"
            md="4"
            sm="12"
            class="font-weight-bold"
          >
            Variant Dihapus:
          </b-col>
          <b-col
            xl="9"
            md="8"
            sm="12"
          >
            <span
              v-if="activeCartProduct.variant.variant"
              class="text-nowrap"
            >
              {{ activeCartProduct.variant.variant }}
            </span>
            <span
              v-else-if="activeCartProduct.variant.sku_name"
              class="text-nowrap"
            >
              {{ activeCartProduct.variant.sku_name }}
            </span>
            <i
              v-else
              class="text-nowrap text-secondary"
            >Tanpa Nama</i>
            ({{ toCurrency(activeCartProduct.variant.price) }})
          </b-col>
        </b-row>

        <validation-observer ref="simpleRules">
          <b-form
            class="mt-2"
            @submit.prevent="submitUpdateVariant"
          >
            <b-row>
              <b-col md="12">
                <b-form-group
                  label="Variant Pengganti"
                  label-for="alternative-variant"
                  description="Catatan: Pastikan memilih variant yang paling mendekati"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Variant Pengganti"
                    rules="required"
                  >
                    <b-form-select
                      id="alternative-variant"
                      v-model="selectedAlternativeVariantId"
                      :options="alternativeVariants"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <b-col
                md="12"
                class="text-right"
              >
                <hr class="mt-0">

                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  type="submit"
                  variant="primary"
                  class="mr-1 mb-1"
                  :disabled="isLoading"
                >
                  <b-spinner
                    v-show="isLoading"
                    small
                  />
                  Simpan
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  BModal,
  BForm,
  BFormGroup,
  BTable,
  BRow,
  BCol,
  BMedia,
  BImg,
  BSkeletonTable,
  BButton,
  BSpinner,
  BFormSelect,
  VBTooltip,
  BBadge,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'

import Pagination from '@/layouts/components/custom/Pagination.vue'
import TableAction from '@/layouts/components/custom/TableAction.vue'

export default {
  components: {
    BSkeletonTable,
    BModal,
    BTable,
    BForm,
    BFormGroup,
    BRow,
    BCol,
    BMedia,
    BImg,
    BButton,
    BSpinner,
    BFormSelect,
    ValidationProvider,
    ValidationObserver,
    BBadge,
    Pagination,
    TableAction,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      tableFields: [
        { key: 'name', label: 'Produk' },
        { key: 'user', label: 'Kontak Pembeli' },
        { key: 'variant', label: 'Variant (Dihapus)' },
        { key: 'price', label: 'Harga' },
        { key: 'actions', label: '', thStyle: { width: '1%' } },
      ],

      activeCartProduct: '',
      selectedAlternativeVariantId: '',

      required,
    }
  },
  computed: {
    ...mapState('cartProducts', ['cartProducts', 'isLoading']),
    query() {
      return this.$route.query
    },
    isShowEditVariant: {
      get() {
        return this.$store.state.cartProducts.isShowEditVariant
      },
      set(value) {
        this.$store.commit('cartProducts/SET_OPEN_EDIT', value)
      },
    },
    alternativeVariants() {
      return [
        { value: '', text: 'Harap pilih' },
        ...this.activeCartProduct.alternative_variants.map(item => {
          let text = ''
          if (item.variant) text = item.variant
          else if (item.sku_name) text = item.sku_name
          else text = 'Tanpa Nama'

          text = `${text} (${this.toCurrency(item.price)})`

          return { value: item.id, text }
        }),
      ]
    },
    tableAction() {
      return [
        {
          access: this.canAccess('manage', 'Product'),
          to: null,
          action: data => { this.changeGiftProduct(data) },
          icon: 'EditIcon',
          title: 'Pilih Variant Pengganti',
        },
      ]
    },
  },
  async created() {
    if (!this.cartProducts.data.length) {
      await this.getCartProducts(this.query)
    }
  },
  methods: {
    ...mapActions('cartProducts', ['getCartProducts', 'updateVariant']),
    changeGiftProduct(value) {
      this.selectedAlternativeVariantId = ''

      this.activeCartProduct = value
      this.isShowEditVariant = true
    },
    submitUpdateVariant() {
      this.$refs.simpleRules.validate().then(async success => {
        if (success) {
          await this.updateVariant({ skuId: this.activeCartProduct.id, alternativeSkuId: this.selectedAlternativeVariantId, query: this.$route.query })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
